import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Save, User, Mail, Phone, Calendar, Coins, ShieldCheck } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { useCustomerAuth } from "@/customer/AuthContext";

const Field = ({ label, icon: Icon, ...rest }) => (
  <label className="block">
    <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2 inline-flex items-center gap-2">
      {Icon && <Icon className="w-3 h-3" />}{label}
    </span>
    <input {...rest} className="w-full bg-black/40 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none font-body focus:border-[#ff2e93] focus:shadow-[0_0_0_3px_rgba(255,46,147,0.10)] disabled:opacity-60" />
  </label>
);

export default function CustomerProfile() {
  const { user, refresh } = useCustomerAuth();
  const [form, setForm] = useState({ name: "", surname: "", phone: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", surname: user.surname || "", phone: user.phone || "" });
  }, [user]);

  const save = async () => {
    setBusy(true);
    try {
      await customerApi.put("/customer/me", form);
      await refresh();
      toast.success("Profile updated.");
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setBusy(false); }
  };

  if (!user) return null;
  return (
    <div data-testid="page-customer-profile" className="max-w-[900px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-8">
      <SEO title="CustomerProfile · WelkomBiz Photography" noindex />
      <div>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— My Profile</p>
        <h1 className="font-display text-4xl sm:text-5xl"><span className="text-chrome">Account &</span> <span className="italic text-magcyan">preferences</span></h1>
      </div>

      <section className="glass-strong chrome-border rounded-md p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-60 h-60 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,46,147,0.15), transparent 70%)" }} />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field data-testid="profile-name" label="Name" icon={User} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field data-testid="profile-surname" label="Surname" icon={User} value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} />
          <Field label="Email" icon={Mail} value={user.email} disabled />
          <Field data-testid="profile-phone" label="Phone" icon={Phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+27 ..." />
        </div>
        <div className="relative mt-6 flex justify-end">
          <button data-testid="profile-save" onClick={save} disabled={busy} className="btn-primary !py-2.5 !px-5 !text-[11px] disabled:opacity-60">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="glass chrome-border rounded-md p-5">
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-1 inline-flex items-center gap-2"><Coins className="w-3 h-3 text-[#ff2e93]" /> Credit Balance</p>
          <p className="font-display text-3xl text-chrome">{user.credit_balance ?? 0}</p>
        </div>
        <div className="glass chrome-border rounded-md p-5">
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-1 inline-flex items-center gap-2"><Calendar className="w-3 h-3 text-[#18d3ff]" /> Member Since</p>
          <p className="text-sm text-white mt-1">{user.created_at ? new Date(user.created_at).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</p>
        </div>
        <div className="glass chrome-border rounded-md p-5">
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-1 inline-flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-[#ff2e93]" /> Account Status</p>
          <p className="text-sm text-white mt-1 capitalize">{user.status || "active"}</p>
        </div>
      </section>
    </div>
  );
}
